import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SearchResultItem {
  id: string;
  kind: "place" | "activity" | "host";
  title: string;
  subtitle?: string;
  href: string;
  imageUrl?: string | null;
}

export interface GroupedResults {
  places: SearchResultItem[];
  activities: SearchResultItem[];
  hosts: SearchResultItem[];
}

const MAX_PER_GROUP = 5;
const MAX_TOTAL = 12;

async function fetchSearchResults(q: string): Promise<GroupedResults> {
  const term = `%${q.trim()}%`;

  const [placesRes, outingsRes, hostsRes] = await Promise.all([
    supabase
      .from("places_explore")
      .select("id, slug, name, city, region, county")
      .ilike("name", term)
      .limit(MAX_PER_GROUP),
    supabase
      .from("outings_public_v" as any)
      .select("id, title, producer_name, start_date")
      .ilike("title", term)
      .limit(MAX_PER_GROUP),
    supabase
      .rpc("get_place_producers_public" as any, {
        p_search_term: q.trim(),
        p_limit: MAX_PER_GROUP,
      })
      .select("producer_id, producer_name, producer_slug"),
  ]);

  const places: SearchResultItem[] = (placesRes.data || []).map((r: any) => ({
    id: String(r.id),
    kind: "place" as const,
    title: r.name || "",
    subtitle: [r.city, r.county || r.region].filter(Boolean).join(" · ") || undefined,
    href: `/place/${r.slug}`,
  }));

  const activities: SearchResultItem[] = (outingsRes.data || []).map((r: any) => {
    const parts: string[] = [];
    if (r.start_date) {
      try {
        const d = new Date(r.start_date);
        parts.push(d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" }));
      } catch { /* skip */ }
    }
    if (r.producer_name) parts.push(r.producer_name);
    return {
      id: String(r.id),
      kind: "activity" as const,
      title: r.title || "",
      subtitle: parts.join(" · ") || undefined,
      href: `/outings/${r.id}`,
    };
  });

  // Dedupe hosts by producer_id
  const seenIds = new Set<string>();
  const hosts: SearchResultItem[] = [];
  for (const r of (hostsRes.data || []) as any[]) {
    if (!r.producer_id || seenIds.has(r.producer_id)) continue;
    seenIds.add(r.producer_id);
    hosts.push({
      id: r.producer_id,
      kind: "host" as const,
      title: r.producer_name || "",
      href: `/producer/${r.producer_slug}`,
    });
  }

  return { places, activities, hosts };
}

export function useGlobalSearch(query: string) {
  const trimmed = query.trim();

  const { data, isLoading } = useQuery({
    queryKey: ["global-search", trimmed],
    queryFn: () => fetchSearchResults(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: 15_000,
  });

  const results = data ?? { places: [], activities: [], hosts: [] };

  const allItems = useMemo(() => {
    const merged = [
      ...results.places,
      ...results.activities,
      ...results.hosts,
    ];
    return merged.slice(0, MAX_TOTAL);
  }, [results]);

  const hasResults = allItems.length > 0;

  return { results, allItems, hasResults, isLoading, isActive: trimmed.length >= 2 };
}
